import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowRight } from '@commutifi-fe/custom-icons/regular';
import { Typography as AntdTypography } from 'antd';
import type { LinkProps as AntdLinkProps } from 'antd/es/typography/Link';
import type { ParagraphProps as AntdParagraphProps } from 'antd/es/typography/Paragraph';
import type { TextProps } from 'antd/es/typography/Text';
import type { TitleProps } from 'antd/es/typography/Title';
import cx from 'classnames';
import { createKeydownFromClick } from 'keydown-from-click';
import { Skeleton } from '../Skeleton';
import './styles.scss';
const {
  Link,
  Paragraph,
  Text,
  Title
} = AntdTypography;
enum FontWeight {
  Regular = 'regular',
  Medium = 'medium',
  SemiBold = 'semi-bold',
  Bold = 'bold',
  Black = 'black',
}
enum TextAlign {
  Left = 'left',
  Right = 'right',
  Center = 'center',
  Justify = 'justify',
}
type FontWeightType = `${FontWeight}`;
enum StyleGuides {
  Marketing = 'marketing',
  Dashboard = 'dashboard',
}
interface TextFormatting {
  capitalize?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
}
interface CommonProps {
  styleguide?: `${StyleGuides}`;
  /**
   * Apply styling of a specific heading but keep
   * the semantic used with prop 'level'
   */
  variant?: 'h1' | 'h2' | 'h3' | 'h4';
  textAlign?: TextAlign | `${TextAlign}`;
}
export interface TitleContainerProps extends TitleProps, TextFormatting, CommonProps {
  bold?: boolean;
  loading?: boolean;
}
export declare type SizeType = 'lead' | 'regular' | 'small';
interface TextContainerProps extends Omit<TextProps, 'strong'>, TextFormatting, CommonProps {
  /**
   * Display: block
   */
  block?: boolean;
  size?: SizeType;
  strong?: FontWeightType;
}
export interface LinkProps extends Omit<AntdLinkProps, 'strong'>, TextFormatting, Pick<CommonProps, 'styleguide'> {
  size?: SizeType;
  strong?: FontWeightType;
  styleguide?: `${StyleGuides}`;
}
export declare type DirectionType = 'horizontal' | 'vertical';
export interface ParagraphProps extends Omit<AntdParagraphProps, 'strong' | 'direction'>, TextFormatting {
  size?: SizeType;
  // Weather to display the expand button beside the text (horizontal) or under it (vertical)
  direction?: DirectionType;
  strong?: FontWeightType;
  textAlign?: TextAlign;
}
const applyTextFormattingClassNames = ({
  capitalize,
  uppercase,
  lowercase
}: Pick<TitleContainerProps, 'uppercase' | 'lowercase' | 'capitalize'>) => ({
  [`${AppTypographyClassName}--capitalize`]: capitalize,
  [`${AppTypographyClassName}--uppercase`]: uppercase,
  [`${AppTypographyClassName}--lowercase`]: lowercase
});
const AppTitleClassName = 'app-typography-title';
function TitleContainer(t0) {
  const $ = _c(34);
  let capitalize;
  let children;
  let className;
  let loading;
  let lowercase;
  let props;
  let ref;
  let style;
  let t1;
  let t2;
  let t3;
  let textAlign;
  let uppercase;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      bold: t1,
      level: t2,
      style,
      styleguide: t3,
      loading,
      children,
      variant,
      capitalize,
      lowercase,
      uppercase,
      textAlign,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = capitalize;
    $[2] = children;
    $[3] = className;
    $[4] = loading;
    $[5] = lowercase;
    $[6] = props;
    $[7] = ref;
    $[8] = style;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = textAlign;
    $[13] = uppercase;
    $[14] = variant;
  } else {
    capitalize = $[1];
    children = $[2];
    className = $[3];
    loading = $[4];
    lowercase = $[5];
    props = $[6];
    ref = $[7];
    style = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    textAlign = $[12];
    uppercase = $[13];
    variant = $[14];
  }
  const bold = t1 === undefined ? true : t1;
  const level = t2 === undefined ? 1 : t2;
  const styleguide = t3 === undefined ? StyleGuides.Dashboard : t3;
  const t4 = !bold && `${AppTitleClassName}--normal-fw`;
  const t5 = styleguide === StyleGuides.Marketing && `${AppTitleClassName}--marketing`;
  const t6 = variant && `${AppTitleClassName}-variant--${variant}`;
  let t7;
  if ($[15] !== capitalize || $[16] !== className || $[17] !== lowercase || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== textAlign || $[22] !== uppercase) {
    t7 = cx(AppTitleClassName, t4, t5, t6, applyTextFormattingClassNames({
      capitalize,
      lowercase,
      uppercase
    }), textAlign && `${AppTypographyClassName}-text-align--${textAlign}`, className);
    $[15] = capitalize;
    $[16] = className;
    $[17] = lowercase;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = textAlign;
    $[22] = uppercase;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== children || $[25] !== loading) {
    t8 = loading ? <Skeleton.Input /> : children;
    $[24] = children;
    $[25] = loading;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== level || $[28] !== props || $[29] !== ref || $[30] !== style || $[31] !== t7 || $[32] !== t8) {
    t9 = <Title ref={ref} className={t7} level={level} style={style} {...props}>{t8}</Title>;
    $[27] = level;
    $[28] = props;
    $[29] = ref;
    $[30] = style;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
}
const getFontWeightStyle = (strong: `${FontWeight}` | undefined) => ({
  ...(strong === FontWeight.Regular && {
    fontWeight: 400
  }),
  ...(strong === FontWeight.Medium && {
    fontWeight: 500
  }),
  ...(strong === FontWeight.SemiBold && {
    fontWeight: 600
  }),
  ...(strong === FontWeight.Bold && {
    fontWeight: 700
  }),
  ...(strong === FontWeight.Black && {
    fontWeight: 900
  })
});
const AppTypographyClassName = 'app-typography';
function TextContainer(t0) {
  const $ = _c(34);
  let block;
  let capitalize;
  let className;
  let lowercase;
  let props;
  let ref;
  let strong;
  let style;
  let t1;
  let t2;
  let textAlign;
  let uppercase;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      strong,
      styleguide: t2,
      style,
      block,
      variant,
      capitalize,
      lowercase,
      uppercase,
      textAlign,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = block;
    $[2] = capitalize;
    $[3] = className;
    $[4] = lowercase;
    $[5] = props;
    $[6] = ref;
    $[7] = strong;
    $[8] = style;
    $[9] = t1;
    $[10] = t2;
    $[11] = textAlign;
    $[12] = uppercase;
    $[13] = variant;
  } else {
    block = $[1];
    capitalize = $[2];
    className = $[3];
    lowercase = $[4];
    props = $[5];
    ref = $[6];
    strong = $[7];
    style = $[8];
    t1 = $[9];
    t2 = $[10];
    textAlign = $[11];
    uppercase = $[12];
    variant = $[13];
  }
  const size = t1 === undefined ? "regular" : t1;
  const styleguide = t2 === undefined ? StyleGuides.Dashboard : t2;
  const t3 = `${AppTypographyClassName}--${size}`;
  const t4 = block && `${AppTypographyClassName}--block`;
  const t5 = styleguide === StyleGuides.Marketing && `${AppTypographyClassName}--marketing`;
  const t6 = variant && `${AppTitleClassName} ${AppTitleClassName}-variant--${variant}`;
  let t7;
  if ($[14] !== capitalize || $[15] !== className || $[16] !== lowercase || $[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== textAlign || $[22] !== uppercase) {
    t7 = cx(AppTypographyClassName, t3, t4, t5, t6, applyTextFormattingClassNames({
      capitalize,
      lowercase,
      uppercase
    }), textAlign && `${AppTypographyClassName}-text-align--${textAlign}`, className);
    $[14] = capitalize;
    $[15] = className;
    $[16] = lowercase;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = textAlign;
    $[22] = uppercase;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== strong) {
    t8 = getFontWeightStyle(strong);
    $[24] = strong;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== style || $[27] !== t8) {
    t9 = {
      ...style,
      ...t8
    };
    $[26] = style;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== props || $[30] !== ref || $[31] !== t7 || $[32] !== t9) {
    t10 = <Text ref={ref} className={t7} style={t9} {...props} />;
    $[29] = props;
    $[30] = ref;
    $[31] = t7;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
}
function ParagraphContainer(t0) {
  const $ = _c(30);
  let capitalize;
  let className;
  let lowercase;
  let props;
  let ref;
  let strong;
  let style;
  let t1;
  let t2;
  let textAlign;
  let uppercase;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      direction: t2,
      strong,
      style,
      capitalize,
      lowercase,
      uppercase,
      textAlign,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = capitalize;
    $[2] = className;
    $[3] = lowercase;
    $[4] = props;
    $[5] = ref;
    $[6] = strong;
    $[7] = style;
    $[8] = t1;
    $[9] = t2;
    $[10] = textAlign;
    $[11] = uppercase;
  } else {
    capitalize = $[1];
    className = $[2];
    lowercase = $[3];
    props = $[4];
    ref = $[5];
    strong = $[6];
    style = $[7];
    t1 = $[8];
    t2 = $[9];
    textAlign = $[10];
    uppercase = $[11];
  }
  const size = t1 === undefined ? "regular" : t1;
  const direction = t2 === undefined ? "horizontal" : t2;
  const t3 = `${AppTypographyClassName}--${size}`;
  const t4 = direction === "vertical" && `${AppTypographyClassName}-expand--bottom`;
  let t5;
  if ($[12] !== capitalize || $[13] !== className || $[14] !== lowercase || $[15] !== t3 || $[16] !== t4 || $[17] !== textAlign || $[18] !== uppercase) {
    t5 = cx(AppTypographyClassName, t3, t4, applyTextFormattingClassNames({
      capitalize,
      lowercase,
      uppercase
    }), textAlign && `${AppTypographyClassName}-text-align--${textAlign}`, className);
    $[12] = capitalize;
    $[13] = className;
    $[14] = lowercase;
    $[15] = t3;
    $[16] = t4;
    $[17] = textAlign;
    $[18] = uppercase;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== strong) {
    t6 = getFontWeightStyle(strong);
    $[20] = strong;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== style || $[23] !== t6) {
    t7 = {
      ...style,
      ...t6
    };
    $[22] = style;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== props || $[26] !== ref || $[27] !== t5 || $[28] !== t7) {
    t8 = <Paragraph ref={ref} className={t5} style={t7} {...props} />;
    $[25] = props;
    $[26] = ref;
    $[27] = t5;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
function LinkContainer(t0) {
  const $ = _c(31);
  let capitalize;
  let className;
  let lowercase;
  let onClick;
  let props;
  let ref;
  let strong;
  let style;
  let t1;
  let uppercase;
  if ($[0] !== t0) {
    ({
      className,
      strong,
      size: t1,
      style,
      onClick,
      capitalize,
      lowercase,
      uppercase,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = capitalize;
    $[2] = className;
    $[3] = lowercase;
    $[4] = onClick;
    $[5] = props;
    $[6] = ref;
    $[7] = strong;
    $[8] = style;
    $[9] = t1;
    $[10] = uppercase;
  } else {
    capitalize = $[1];
    className = $[2];
    lowercase = $[3];
    onClick = $[4];
    props = $[5];
    ref = $[6];
    strong = $[7];
    style = $[8];
    t1 = $[9];
    uppercase = $[10];
  }
  const size = t1 === undefined ? "regular" : t1;
  const t2 = `${AppTypographyClassName}--${size}`;
  let t3;
  if ($[11] !== capitalize || $[12] !== className || $[13] !== lowercase || $[14] !== t2 || $[15] !== uppercase) {
    t3 = cx(t2, applyTextFormattingClassNames({
      capitalize,
      lowercase,
      uppercase
    }), className);
    $[11] = capitalize;
    $[12] = className;
    $[13] = lowercase;
    $[14] = t2;
    $[15] = uppercase;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== strong) {
    t4 = getFontWeightStyle(strong);
    $[17] = strong;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== style || $[20] !== t4) {
    t5 = {
      display: "inline-flex",
      alignItems: "center",
      ...style,
      ...t4
    };
    $[19] = style;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== onClick) {
    t6 = onClick ? createKeydownFromClick(onClick, {
      keys: ["Enter"]
    }) : undefined;
    $[22] = onClick;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== onClick || $[25] !== props || $[26] !== ref || $[27] !== t3 || $[28] !== t5 || $[29] !== t6) {
    t7 = <Link ref={ref} role="link" className={t3} style={t5} onClick={onClick} onKeyDown={t6} {...props} />;
    $[24] = onClick;
    $[25] = props;
    $[26] = ref;
    $[27] = t3;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
}
function FormattedEOL({
  children
}: {
  children: string;
}) {
  return children.split('\n').map((paragraph, i) => <React.Fragment key={i}>
      {paragraph}
      <br />
    </React.Fragment>);
}

/**
 * This class is a wrap around [antd Typography](https://ant.design/components/typography/) component extending some of
 * the properties to follow our styling guidelines.
 *
 * Please refer to antd documentation for more details.
 *
 * It is used to insure we are consistent in our use of different text styles and that we normalize
 * our title and text sizes, colors and more. It also makes it easy to use a specific type of text,
 * paragraph, title, etc. into any other component
 */
function InternalTypography(t0) {
  const $ = _c(6);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  let t1;
  if ($[3] !== props || $[4] !== ref) {
    t1 = <AntdTypography ref={ref} {...props} className="app-typography" />;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
type TypographyType = typeof InternalTypography;
interface TypographyInterface extends TypographyType {
  Link: typeof LinkContainer;
  Paragraph: typeof ParagraphContainer;
  Text: typeof TextContainer;
  Title: typeof TitleContainer;
  ExternalLink: typeof ExternalLinkContainer;
  EOLText: typeof FormattedEOL;
  displayName?: string;
}
export const Typography = InternalTypography as TypographyInterface;
Typography.Link = LinkContainer;
Typography.Paragraph = ParagraphContainer;
Typography.Text = TextContainer;
Typography.Title = TitleContainer;
Typography.EOLText = FormattedEOL;
Typography.displayName = AntdTypography.displayName;
function LinkArrowIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      rotate: -45
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <FontAwesomeIcon icon={faLongArrowRight} transform={t1} className={className} />;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function ExternalLinkContainer(t0) {
  const $ = _c(24);
  let children;
  let icon;
  let props;
  let ref;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      iconPosition: t1,
      icon,
      style,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = icon;
    $[3] = props;
    $[4] = ref;
    $[5] = style;
    $[6] = t1;
  } else {
    children = $[1];
    icon = $[2];
    props = $[3];
    ref = $[4];
    style = $[5];
    t1 = $[6];
  }
  const iconPosition = t1 === undefined ? "suffix" : t1;
  let t2;
  if ($[7] !== style) {
    t2 = {
      display: "inline-flex",
      ...style
    };
    $[7] = style;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== icon || $[10] !== iconPosition) {
    t3 = iconPosition === "prefix" ? icon === false ? null : <span>{icon || <LinkArrowIcon className="u-margin-right--small" />}</span> : null;
    $[9] = icon;
    $[10] = iconPosition;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== icon || $[13] !== iconPosition) {
    t4 = iconPosition === "suffix" ? icon === false ? null : <span>{icon || <LinkArrowIcon className="u-margin-left--small" />}</span> : null;
    $[12] = icon;
    $[13] = iconPosition;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== children || $[16] !== t3 || $[17] !== t4) {
    t5 = <span>{t3}{children}{t4}</span>;
    $[15] = children;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== props || $[20] !== ref || $[21] !== t2 || $[22] !== t5) {
    t6 = <LinkContainer ref={ref} underline={true} target="__blank" strong="semi-bold" style={t2} {...props} data-testid="external-link">{t5}</LinkContainer>;
    $[19] = props;
    $[20] = ref;
    $[21] = t2;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
Typography.ExternalLink = ExternalLinkContainer;
export default Typography;